import Icon from 'components/atoms/Icon';
import Loader from 'components/atoms/Loader/Loader';
import { HelmetAndPageAnnouncer } from 'components/organisms/HelmetAndPageAnnouncer/HelmetAndPageAnnouncer';
import {
  DeiStageActionCandidateStatus,
  StageCandidateStatus,
  StageStatus,
  useDeiStageActionCandidateSubmitResponsesMutation,
  useGetDeiStageActionCandidateQuery,
} from 'generated/graphql';
import { FC, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import DeiFormFields from './DeiFormFields';
import { getDeiFieldsWithOptions } from './StageActionDeiForm.helpers';
import styles from './StageActionDeiForm.module.scss';

interface IDeiFormView {
  stageCandidateId: number;
  /** Exit target when the form is already completed, the stage is closed, or
   * loading fails. */
  exitUrl: string;
  /** Where to send the candidate when they haven't given consent. */
  introUrl: string;
  onSubmitted: () => void;
  /** Hosting flow's navigation chrome (back link / navbar). */
  navbar?: ReactNode;
}

/** The DEI questionnaire itself, decoupled from routing — hosted by the
 * role-scoped actions flow (RoleStageDeiForm). */
const DeiFormView: FC<IDeiFormView> = ({
  stageCandidateId,
  exitUrl,
  introUrl,
  onSubmitted,
  navbar,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { handleMsgType } = useNotification();

  const { data: stageData, loading } = useGetDeiStageActionCandidateQuery({
    variables: { stageCandidateId },
    onError: error => {
      handleMsgType({
        type: TNotification.error,
        message: error?.message,
      });

      navigate(exitUrl);
    },
    onCompleted: data => {
      const stageStatus = data.StageCandidateFindById?.stage.status;
      const stageCandidateStatus = data.StageCandidateFindById?.status;
      const deiCandidate = data.StageCandidateFindById?.deiStageActionCandidate;
      const deiStatus = deiCandidate?.status;

      if (
        stageStatus === StageStatus.Expired ||
        stageStatus === StageStatus.Scheduled ||
        deiStatus === DeiStageActionCandidateStatus.Submitted ||
        deiStatus === DeiStageActionCandidateStatus.Declined ||
        stageCandidateStatus === StageCandidateStatus.Completed ||
        stageCandidateStatus === StageCandidateStatus.SignedOff ||
        stageCandidateStatus === StageCandidateStatus.ManagerCompleted
      ) {
        navigate(exitUrl);
      } else if (deiCandidate && !deiCandidate.hasActiveConsent) {
        navigate(introUrl);
      }
    },
    fetchPolicy: 'network-only',
  });

  const [submitDeiResponses, { loading: loadingSubmit }] =
    useDeiStageActionCandidateSubmitResponsesMutation({
      onCompleted: onSubmitted,
      onError: error => {
        handleMsgType({
          type: TNotification.error,
          message: error?.message,
        });
      },
    });

  const deiData = useMemo(() => {
    const deiStageActionCandidate =
      stageData?.StageCandidateFindById?.deiStageActionCandidate;

    const formFields = deiStageActionCandidate
      ? getDeiFieldsWithOptions(deiStageActionCandidate.stageAction.fields)
      : [];

    const responses = deiStageActionCandidate?.responses;

    return { formFields, responses };
  }, [stageData]);

  const handleSubmitDeiForm = (payload: {
    [key: string]: string | string[];
  }) => {
    submitDeiResponses({
      variables: {
        responses: payload,
        stageCandidateId,
      },
    });
  };

  if (loading) {
    return (
      <div className="rs-form__LoaderWrapper">
        <Loader />
      </div>
    );
  }
  return (
    <div className={styles.container}>
      <HelmetAndPageAnnouncer pageTitle={t('test.instructions.dei.name')} />
      {navbar}
      <div className={styles.header}>
        <Icon icon="dei" aria-hidden />
        <div>{t('test.instructions.dei.name')}</div>
      </div>
      <div className={styles.formWrapper} data-testid="stage-action__dei-form">
        {deiData.responses ? (
          <DeiFormFields
            formFields={deiData.formFields}
            loadingSubmit={loadingSubmit}
            onSubmit={handleSubmitDeiForm}
            responses={deiData.responses}
          />
        ) : null}
      </div>
    </div>
  );
};

export default DeiFormView;
